import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { TrendingUp, TrendingDown, DollarSign, Package, Clock, RotateCw } from "lucide-react";

export default function AdminProjectProfit() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/finance/project-profit"); setData(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = (data?.rows || []).filter((r) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (r.title || "").toLowerCase().includes(needle) ||
           (r.project_number || "").toLowerCase().includes(needle) ||
           (r.customer_number || "").toLowerCase().includes(needle);
  });

  return (
    <div data-testid="admin-project-profit" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Finance · Reports</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
            Profit per Project
          </h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            Revenue (invoices) minus direct costs (materials × cost + labor hours × rate).
            {data && <> Labor rate: <b>${data.labor_rate_per_hour}/hr</b>.</>}
          </p>
        </div>
        <button onClick={load} className="btn-outline-navy text-xs inline-flex items-center gap-1" data-testid="refresh-profit">
          <RotateCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {data && (
        <div className="grid md:grid-cols-4 gap-4">
          <StatCard label="Revenue invoiced" value={`$${data.totals.revenue_invoiced.toLocaleString()}`} icon={DollarSign} />
          <StatCard label="Material cost"     value={`$${data.totals.material_cost.toLocaleString()}`} icon={Package} />
          <StatCard label="Labor cost"        value={`$${data.totals.labor_cost.toLocaleString()}`} icon={Clock} />
          <StatCard
            label="Total margin"
            value={`$${data.totals.margin.toLocaleString()}`}
            hint={`${data.totals.margin_pct}%`}
            accent={data.totals.margin >= 0}
            icon={data.totals.margin >= 0 ? TrendingUp : TrendingDown}
          />
        </div>
      )}

      <div className="card-pb p-3">
        <input
          type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search project title, number, or customer…"
          data-testid="profit-search"
          className="w-full border-0 focus:ring-0 bg-transparent text-sm dark:text-white"
        />
      </div>

      <div className="card-pb overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] dark:bg-slate-800 text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
            <tr>
              <th className="text-left px-4 py-2">Project</th>
              <th className="text-right px-4 py-2">Revenue</th>
              <th className="text-right px-4 py-2">Materials</th>
              <th className="text-right px-4 py-2">Labor</th>
              <th className="text-right px-4 py-2">Margin</th>
              <th className="text-right px-4 py-2">%</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={6} className="text-center py-8 text-[#94A3B8]">Loading…</td></tr>)}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-[#94A3B8] italic">No projects match.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.project_id} data-testid={`profit-row-${r.project_id}`}
                  className="border-t border-[#E2E8F0] dark:border-slate-700 hover:bg-[#F8FAFC] dark:hover:bg-slate-800">
                <td className="px-4 py-2">
                  <div className="text-[#0A2540] dark:text-white">{r.title}</div>
                  <div className="text-[10px] text-[#94A3B8] font-mono">
                    {r.project_number || r.project_id} · {r.customer_number || "—"}
                  </div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  ${r.revenue_invoiced.toLocaleString()}
                  {r.revenue_paid !== r.revenue_invoiced && (
                    <div className="text-[10px] text-[#94A3B8]">paid ${r.revenue_paid.toLocaleString()}</div>
                  )}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">${r.material_cost.toLocaleString()}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  ${r.labor_cost.toLocaleString()}
                  {r.labor_hours > 0 && (
                    <div className="text-[10px] text-[#94A3B8]">{r.labor_hours}h</div>
                  )}
                </td>
                <td className={`px-4 py-2 text-right tabular-nums font-medium ${
                  r.margin > 0 ? "text-emerald-600" : r.margin < 0 ? "text-red-500" : "text-[#94A3B8]"
                }`}>${r.margin.toLocaleString()}</td>
                <td className={`px-4 py-2 text-right tabular-nums text-xs font-bold ${
                  r.margin_pct > 20 ? "text-emerald-600" : r.margin_pct < 0 ? "text-red-500" : "text-amber-500"
                }`}>{r.margin_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, hint, accent, icon: Icon }) {
  const good = accent === true;
  const bad  = accent === false;
  return (
    <div className={`card-pb p-5 ${good ? "border-l-4 border-emerald-500" : bad ? "border-l-4 border-red-500" : ""}`}
         data-testid={`profit-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="inline-flex items-center gap-1.5">
        <Icon className={`w-3 h-3 ${good ? "text-emerald-500" : bad ? "text-red-500" : "text-[#C9A356]"}`} />
        <div className="text-[10px] uppercase tracking-wider font-bold text-[#94A3B8]">{label}</div>
      </div>
      <div className={`text-2xl font-heading mt-2 ${good ? "text-emerald-600" : bad ? "text-red-500" : "text-[#0A2540] dark:text-white"}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-[#94A3B8] mt-0.5">{hint}</div>}
    </div>
  );
}
